import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Phone, Plus, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import CrmShell from "@/components/crm/CrmShell";
import LeadDrawer from "@/components/crm/LeadDrawer";
import { Panel, TempBadge, ago, inputCls } from "@/components/crm/ui";
import { BUDGETS, CORRIDORS, PROPERTY_TYPES, STAGES, corridorLabel, leadWaLink, stageLabel } from "@/config/brand";
import { useAuth } from "@/context/AuthContext";

const NEW_LEAD = {
  name: "",
  phone: "",
  corridor: "wave_city",
  property_type: "apartment",
  budget: BUDGETS[1],
  temperature: "warm",
  assigned_to: "",
};

export default function LeadsList() {
  const { isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(NEW_LEAD);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    try {
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    const poll = setInterval(load, 20000);
    return () => clearInterval(poll);
  }, [load]);

  useEffect(() => {
    api.get("/users").then(({ data }) => setEmployees(data.filter((u) => u.role === "employee")));
  }, []);

  const contact = async (id, channel) => {
    try {
      await api.post(`/leads/${id}/contact?channel=${channel}`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const createLead = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, source: "manual" };
      if (!payload.assigned_to) delete payload.assigned_to;
      await api.post("/leads", payload);
      toast.success("Lead created");
      setForm(NEW_LEAD);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <CrmShell
      title="Leads"
      subtitle={`${leads.length} lead${leads.length === 1 ? "" : "s"} in view · tap to call or WhatsApp instantly`}
      actions={
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="refresh-leads-btn"
            className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-ink hover:border-brand"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={() => setCreating((v) => !v)}
            data-testid="toggle-create-lead"
            className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white"
          >
            {creating ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {creating ? "Cancel" : "New lead"}
          </button>
        </div>
      }
    >
      {creating && (
        <Panel className="mb-5" data-testid="create-lead-panel">
          <form onSubmit={createLead} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-name-input" className={inputCls} />
            <input required placeholder="Phone (+91…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="create-phone-input" className={inputCls} />
            <select value={form.corridor} onChange={(e) => setForm({ ...form, corridor: e.target.value })} data-testid="create-corridor-select" className={inputCls}>
              {CORRIDORS.map((c) => (
                <option key={c.key} value={c.key} className="bg-white">{c.label}</option>
              ))}
            </select>
            <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inputCls}>
              {PROPERTY_TYPES.map((p) => (
                <option key={p} value={p} className="bg-white">{p}</option>
              ))}
            </select>
            <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className={inputCls}>
              {BUDGETS.map((b) => (
                <option key={b} value={b} className="bg-white">{b}</option>
              ))}
            </select>
            {isAdmin && (
              <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="create-assign-select" className={inputCls}>
                <option value="" className="bg-white">Unassigned</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id} className="bg-white">{emp.name}</option>
                ))}
              </select>
            )}
            <button type="submit" data-testid="create-lead-submit" className="rounded-full bg-brand py-2.5 text-xs font-bold text-white sm:col-span-3">
              Save lead
            </button>
          </form>
        </Panel>
      )}

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone or email"
            data-testid="leads-search-input"
            className={`${inputCls} pl-9`}
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-status-filter" className={`${inputCls} sm:w-52`}>
          <option value="all" className="bg-white">All stages</option>
          {STAGES.map((s) => (
            <option key={s.key} value={s.key} className="bg-white">{s.label}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2.5" data-testid="leads-list">
        {leads.length === 0 && <p className="py-10 text-center text-sm text-muted2">No leads match this view.</p>}
        {leads.map((l) => (
          <div key={l.id} className="rounded-2xl border border-line bg-white p-4" data-testid={`lead-row-${l.id}`}>
            <div className="flex items-start justify-between gap-3">
              <button onClick={() => setOpenId(l.id)} data-testid={`lead-open-${l.id}`} className="text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ink">{l.name}</span>
                  <TempBadge temp={l.temperature} />
                </div>
                <div className="mt-1 text-[11px] text-body">
                  {l.phone} · {corridorLabel(l.corridor)} · {l.budget || "budget NA"}
                </div>
                <div className="mt-1 text-[11px] text-muted2">
                  {stageLabel(l.status)} · {l.assigned_to_name || "Unassigned"} · {ago(l.created_at)}
                </div>
              </button>
              <div className="flex shrink-0 gap-2">
                <a
                  href={`tel:${l.phone}`}
                  onClick={() => contact(l.id, "call")}
                  data-testid={`lead-call-${l.id}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white active:scale-95"
                  aria-label="Call lead"
                >
                  <Phone className="h-4 w-4" />
                </a>
                <a
                  href={leadWaLink(l)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => contact(l.id, "whatsapp")}
                  data-testid={`lead-whatsapp-${l.id}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-brand/40 text-brand active:scale-95"
                  aria-label="WhatsApp lead"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      <LeadDrawer
        leadId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        employees={employees}
        onChanged={load}
      />
    </CrmShell>
  );
}
